import React from "react";
import { Link } from "react-router-dom";
import { Clock, Coffee, ChevronRight, Check, FileDown, Smartphone, Wallet, CalendarCheck, Sparkles, UserPlus, Play, Building2, HelpCircle, MousePointerClick, Eye, FileSpreadsheet, Send, KeyRound } from "lucide-react";

const YASAMARU_HERO = "/yasamaru/hero.png";
const YASAMARU_CHEER = "/yasamaru/cheer.png";

const SHOT_ADMIN_REPORT = "/screenshots/admin_report.png";
const SHOT_ADMIN_ATTENDANCE = "/screenshots/admin_attendance.png";
const SHOT_EMPLOYEE = "/screenshots/employee_dashboard.png";
const SHOT_MONTHLY = "/screenshots/monthly.png";
const SHOT_MOBILE_ATTENDANCE = "/screenshots/mobile_admin_attendance.png";
const SHOT_MOBILE_REPORT = "/screenshots/mobile_admin_report.png";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] relative overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/85 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 shrink-0">
            <span className="text-xl md:text-2xl font-display font-black text-stone-900">やさまるタイム</span>
            <span className="hidden md:inline tag bg-emerald-100 text-emerald-800 text-[10px]">やさまるシリーズ</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1 text-sm">
            <NavLink href="#features" testid="nav-features">特徴</NavLink>
            <NavLink href="#screens" testid="nav-screens">画面</NavLink>
            <NavLink href="#how" testid="nav-how">導入のながれ</NavLink>
            <NavLink href="#pricing" testid="nav-pricing">料金</NavLink>
            <NavLink href="#faq" testid="nav-faq">よくある質問</NavLink>
            <NavLink href="#contact" testid="nav-contact">お問い合わせ</NavLink>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/login" data-testid="nav-login-link" className="rounded-full border-2 border-stone-900 text-stone-900 px-4 md:px-5 py-1.5 text-sm font-semibold hover:bg-stone-100 transition-colors">ログイン</Link>
            <Link to="/signup" data-testid="nav-signup-link" className="rounded-full bg-stone-900 text-white px-4 md:px-5 py-2 text-sm font-semibold hover:bg-stone-700 transition-colors">企業登録</Link>
          </div>
        </div>
        {/* Mobile sub-menu */}
        <div className="md:hidden border-t border-stone-100 overflow-x-auto">
          <div className="flex gap-1 px-3 py-2 text-xs whitespace-nowrap">
            <MobileNav href="#features">特徴</MobileNav>
            <MobileNav href="#screens">画面</MobileNav>
            <MobileNav href="#how">導入のながれ</MobileNav>
            <MobileNav href="#pricing">料金</MobileNav>
            <MobileNav href="#faq">FAQ</MobileNav>
            <MobileNav href="#contact">お問い合わせ</MobileNav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="blob bg-emerald-200" style={{ width: 380, height: 380, top: -60, left: -120 }} />
        <div className="blob bg-lime-200" style={{ width: 320, height: 320, top: 60, right: -80 }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <span className="tag bg-white border border-stone-200 text-stone-600 mb-5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              スタッフ登録だけで、すぐスタート。
            </span>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-stone-900 leading-tight">
              打刻も、人件費集計も、<br />
              <span className="text-emerald-600">これひとつで</span>。
            </h1>
            <p className="mt-6 text-stone-600 text-lg max-w-lg leading-relaxed">
              スタッフの名前を登録するだけで、今すぐ打刻スタート。
              共有画面でワンタップ打刻、人件費の目安、CSV出力まで、
              これひとつで完結します。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-signup-cta" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-7 py-3.5 shadow-sm">
                今すぐはじめる <ChevronRight size={18} />
              </Link>
              <a href="#screens" className="inline-flex items-center gap-2 rounded-full bg-white border border-stone-200 text-stone-800 font-semibold px-7 py-3.5 hover:bg-stone-50">
                画面を見る
              </a>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 md:gap-4 max-w-lg">
              <BigStat label="従業員" value="30名" suffix="までOK！" tone="emerald" />
              <BigStat label="お試し" value="2週間" suffix="無料" tone="amber" />
              <BigStat label="導入時間" value="3分" suffix="で完了" tone="indigo" />
            </div>
          </div>

          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 m-auto h-80 w-80 rounded-full bg-gradient-to-br from-emerald-100 to-lime-100" />
            <img
              src={YASAMARU_HERO}
              alt="やさまる"
              className="relative z-10 max-w-md w-full float-slow drop-shadow-xl"
              data-testid="hero-mascot"
            />
          </div>
        </div>
      </section>

      {/* Two sides — for employees / for admins */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-20">
        <div className="text-center mb-12">
          <span className="tag bg-white border border-stone-200 text-stone-600 mb-3">
            <Sparkles size={12} /> やさまるタイムが選ばれる理由
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 leading-tight">
            従業員にも、管理者にも、<br className="md:hidden" />やさしい。
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {/* For employees */}
          <div className="relative card-soft p-7 md:p-9 bg-gradient-to-br from-emerald-50 via-white to-lime-50 border border-emerald-100 overflow-hidden flex flex-col">
            <span className="tag bg-emerald-500 text-white mb-4 self-start"><MousePointerClick size={12} /> 従業員</span>
            <h3 className="font-display text-2xl md:text-3xl font-black text-stone-900 leading-snug">
              説明しなくても、<br />
              <span className="text-emerald-600">押せばわかる</span>。
            </h3>
            <p className="text-stone-600 mt-3 leading-relaxed text-sm md:text-base">
              共有のタブレットやPCを開いて、自分の名前を選ぶだけ。
              ログインもアプリも不要、はじめての人でも迷いません。
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
              <Bullet>店頭・事務所の1台で全員が打刻</Bullet>
              <Bullet>スマホ・タブレット・PCどれでも同じ画面</Bullet>
              <Bullet>パスワード覚える必要なし</Bullet>
            </ul>
            <div className="mt-6 relative">
              <div className="absolute -inset-2 bg-emerald-200/40 rounded-2xl blur-xl" />
              <img
                src={SHOT_EMPLOYEE}
                alt="共有打刻端末"
                data-testid="two-sides-emp-shot"
                className="relative w-full rounded-xl border border-emerald-200 shadow-lg"
              />
            </div>
          </div>

          {/* For admins */}
          <div className="relative card-soft p-7 md:p-9 bg-gradient-to-br from-indigo-50 via-white to-violet-50 border border-indigo-100 overflow-hidden flex flex-col">
            <span className="tag bg-indigo-500 text-white mb-4 self-start"><Eye size={12} /> 管理者</span>
            <h3 className="font-display text-2xl md:text-3xl font-black text-stone-900 leading-snug">
              知りたいことが、<br />
              <span className="text-indigo-600">すぐ見える</span>。
            </h3>
            <p className="text-stone-600 mt-3 leading-relaxed text-sm md:text-base">
              チーム全員の今日の打刻、月の人件費目安、
              気になる数字がひと目で分かります。
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
              <Bullet>勤怠一覧はリアルタイム更新</Bullet>
              <Bullet>人件費目安・実労働をKPIカードで即確認</Bullet>
              <Bullet>外出先でもスマホでチェック可能</Bullet>
            </ul>
            <div className="mt-6 relative flex justify-center items-end gap-3">
              <div className="absolute inset-0 m-auto h-72 w-72 rounded-full bg-indigo-200/40 blur-2xl" />
              <div className="relative" style={{ width: 175, marginBottom: 20 }}>
                <div className="absolute inset-0 -m-2 rounded-[2rem] bg-stone-900" />
                <img
                  src={SHOT_MOBILE_ATTENDANCE}
                  alt="管理者の勤務状況（モバイル）"
                  data-testid="two-sides-admin-shot-attendance"
                  className="relative rounded-[1.6rem] shadow-2xl border-[5px] border-stone-900"
                />
              </div>
              <div className="relative" style={{ width: 175 }}>
                <div className="absolute inset-0 -m-2 rounded-[2rem] bg-stone-900" />
                <img
                  src={SHOT_MOBILE_REPORT}
                  alt="管理者の月次レポート（モバイル）"
                  data-testid="two-sides-admin-shot-report"
                  className="relative rounded-[1.6rem] shadow-2xl border-[5px] border-stone-900"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900 mb-3">毎日に、ちょうどよく寄り添う。</h2>
        <p className="text-stone-600 mb-10 max-w-2xl">ワンタップ打刻から人件費集計・CSV出力まで。小さなチームに必要なものだけを、ちょうどよく。</p>

        <div className="grid md:grid-cols-3 gap-6">
          <FeatureCard icon={<Clock className="text-emerald-600" />} title="共有画面でワンタップ打刻" desc="店舗のタブレットや受付PCを開けば、スタッフが自分の名前をタップして打刻完了。ログイン不要。" tone="emerald" />
          <FeatureCard icon={<Wallet className="text-emerald-600" />} title="人件費をタイムリー確認" desc="時給を入力するだけで、その月の人件費目安を従業員ごと&合計でリアルタイム集計。" tone="emerald" highlight />
          <FeatureCard icon={<Coffee className="text-orange-500" />} title="休憩のあとから補完" desc="打ち忘れた休憩は、スタッフ別の月次画面からデフォ休憩(例:60分)をワンクリックで適用できます。" tone="orange" highlight />
          <FeatureCard icon={<FileDown className="text-rose-500" />} title="CSV出力" desc="月次の打刻・実労働・人件費をワンクリックでCSVダウンロード。給与計算や経理に。" tone="rose" highlight />
          <FeatureCard icon={<Smartphone className="text-indigo-500" />} title="管理者もスマホ対応" desc="勤怠一覧も月次レポートも、管理者がスマホから確認・操作できます。" tone="indigo" highlight />
          <FeatureCard icon={<CalendarCheck className="text-emerald-600" />} title="個人別の月次一覧" desc="管理画面のレポートからスタッフ名をタップすれば、その人の月次打刻一覧&編集が可能。" tone="emerald" />
        </div>
      </section>

      {/* Screens — actual product shots */}
      <section id="screens" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="text-center mb-10">
          <span className="tag bg-emerald-50 text-emerald-700 mb-3"><Sparkles size={12} />実際の画面</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">触ってみたくなる、やさしいUI。</h2>
          <p className="text-stone-600 mt-3">本物のアプリ画面をそのままお見せします。</p>
        </div>

        <div className="relative card-soft p-3 md:p-5 bg-gradient-to-br from-emerald-50 via-white to-lime-50 border border-emerald-100">
          <div className="grid md:grid-cols-5 gap-5 items-center">
            <div className="md:col-span-2 px-3 md:px-5">
              <span className="tag bg-emerald-100 text-emerald-800"><Wallet size={12} /> 新機能</span>
              <h3 className="font-display text-2xl md:text-3xl font-black text-stone-900 mt-3">タイムリーに、人件費の目安。</h3>
              <p className="text-stone-600 mt-3 text-sm md:text-base leading-relaxed">
                従業員ごとの時給を設定するだけで、その月の実労働時間から
                <strong className="text-emerald-700">人件費の目安が自動計算</strong>。
                経営判断のスピードがぐっと上がります。
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
                <Bullet>合計人件費（月次）をひと目で確認</Bullet>
                <Bullet>従業員ごとの内訳もリアルタイム</Bullet>
                <Bullet>時給はインライン編集で即反映</Bullet>
              </ul>
            </div>
            <div className="md:col-span-3">
              <img src={SHOT_ADMIN_REPORT} alt="月次レポート画面" className="w-full rounded-xl shadow-xl border border-stone-200" data-testid="shot-report" />
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-6">
          <ScreenCard tag="共有端末で全員打刻" title="共有打刻端末" desc="店頭・事務所の1台で、自分の名前をタップして出退勤・休憩。スタッフ全員のログイン不要。" img={SHOT_EMPLOYEE} testid="shot-employee" />
          <ScreenCard tag="チーム全員を一画面で" title="勤怠一覧" desc="今日誰が出勤して、誰が休憩中で、誰が退勤したのかを管理者がひと目で把握。" img={SHOT_ADMIN_ATTENDANCE} testid="shot-attendance" />
          <ScreenCard tag="個人の振り返り" title="月次の打刻一覧" desc="管理画面のレポートからスタッフ名をタップすると、本人の毎日の打刻と実労働がリストで確認・編集できます。" img={SHOT_MONTHLY} testid="shot-monthly" />
        </div>

        <p className="mt-4 text-xs text-stone-400 text-center md:text-right" data-testid="screenshot-disclaimer">
          ※ 画面はデモ用のサンプルです。バージョンや設定により実際の表示と異なる場合があります。
        </p>

        <div className="mt-12 grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3 order-2 md:order-1">
            <span className="tag bg-indigo-100 text-indigo-800"><Smartphone size={12} /> モバイル対応</span>
            <h3 className="font-display text-2xl md:text-3xl font-black text-stone-900 mt-3">管理者も、外出先で勤怠が見える。</h3>
            <p className="text-stone-600 mt-3 leading-relaxed">
              出張中、外回り、休日――そんなときも、管理者はスマホから
              チーム全員の勤怠と人件費をチェックできます。
              専用アプリのインストールは不要。ブラウザでそのまま。
            </p>
            <ul className="mt-4 space-y-1.5 text-sm text-stone-700">
              <Bullet>勤怠一覧をカード表示で見やすく</Bullet>
              <Bullet>月次レポート・人件費もスマホで確認</Bullet>
              <Bullet>1タップで打刻画面と切り替え</Bullet>
            </ul>
          </div>
          <div className="md:col-span-2 order-1 md:order-2 flex items-center justify-center gap-4">
            <div className="relative" style={{ width: 200 }}>
              <div className="absolute inset-0 -m-2 rounded-[2.5rem] bg-stone-900" />
              <img src={SHOT_MOBILE_ATTENDANCE} alt="モバイル勤怠一覧" className="relative rounded-[2rem] shadow-2xl border-[6px] border-stone-900" />
            </div>
            <div className="relative hidden sm:block" style={{ width: 200, marginTop: 30 }}>
              <div className="absolute inset-0 -m-2 rounded-[2.5rem] bg-stone-900" />
              <img src={SHOT_MOBILE_REPORT} alt="モバイル月次レポート" className="relative rounded-[2rem] shadow-2xl border-[6px] border-stone-900" />
            </div>
          </div>
        </div>
      </section>

      {/* How — 3 step setup */}
      <section id="how" className="relative z-10 py-14 md:py-20 bg-soft-mint">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="tag bg-white border border-stone-200 text-stone-600 mb-3"><Play size={12} /> 3ステップ</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">スタッフ登録だけで、今すぐスタート。</h2>
            <p className="text-stone-600 mt-3 max-w-2xl mx-auto">難しい設定はゼロ。最短3分で運用がはじまります。</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Step n={1} icon={<Building2 size={22} />} title="企業を登録" desc="会社名と管理者のメール、専用URLを決めるだけ。" />
            <Step n={2} icon={<UserPlus size={22} />} title="スタッフの名前を登録" desc="従業員の氏名・メール・時給を入力。30名まで一律料金。" highlight />
            <Step n={3} icon={<Play size={22} />} title="すぐに打刻スタート" desc="スタッフが企業URLからログインしてボタンを押すだけ。" />
          </div>
          <div className="text-center mt-10">
            <Link to="/signup" data-testid="how-cta" className="inline-flex items-center gap-2 rounded-full bg-stone-900 text-white font-semibold px-8 py-3.5 hover:bg-stone-700">
              企業を登録して始める <ChevronRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">料金</h2>
          <p className="text-stone-600 mt-3">2週間の無料お試しから。シンプルな1プラン。</p>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="card-soft p-0 border-2 border-emerald-300 relative overflow-hidden" data-testid="pricing-single">
            {/* Eye-catching campaign banner */}
            <div className="relative bg-gradient-to-r from-emerald-500 via-emerald-500 to-lime-500 px-6 md:px-8 py-5 text-white">
              <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-amber-300/30 blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-lime-300/30 blur-2xl" />
              <div className="relative flex items-center gap-3 md:gap-4 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-amber-400 text-amber-950 font-display font-black text-xs md:text-sm px-3 py-1 rounded-full shadow-md">
                  <Sparkles size={14} className="animate-pulse" /> 2週間無料
                </span>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-black text-xl md:text-2xl leading-tight">まずは2週間、無料でお試し</div>
                  <div className="text-xs md:text-sm text-emerald-50 mt-0.5">カード登録不要 / 期間中いつでも解約OK</div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 md:p-10">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="text-sm font-semibold text-stone-600">通常プラン（30名まで一律）</div>
                <span className="tag bg-emerald-100 text-emerald-800 text-xs">¥50 / 人・月相当</span>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <span className="font-display text-6xl font-black text-stone-900">¥1,500</span>
                <span className="text-stone-500 pb-2">/ 月（税抜）</span>
                <span className="text-xs text-stone-500 pb-2.5 ml-1">※ 無料期間終了後</span>
              </div>
              <div className="text-sm text-stone-700 mt-2">
                30名で利用した場合 <span className="font-bold text-emerald-700">1名あたり 月額 ¥50※</span>
              </div>

              <ul className="mt-6 space-y-2.5 text-sm text-stone-700">
                <Bullet>従業員30名まで利用可能</Bullet>
                <Bullet>共有画面ワンタップ打刻・休憩管理</Bullet>
                <Bullet>人件費目安・CSV出力</Bullet>
                <Bullet>個人月次レポート / スマホ対応管理</Bullet>
              </ul>

              <Link to="/signup" data-testid="pricing-cta" className="mt-8 block text-center rounded-full bg-stone-900 hover:bg-stone-700 text-white font-bold py-3.5 text-lg shadow-lg">
                2週間無料ではじめる
              </Link>
              <p className="text-xs text-stone-500 text-center mt-3">クレジットカード登録なしで開始 / いつでも解約可能</p>
            </div>
          </div>

          <p className="text-xs text-stone-500 text-center mt-6 max-w-xl mx-auto" data-testid="pricing-disclaimer">
            ※ 30名で計算した場合の1名あたり目安。<strong className="text-stone-700">31名以上の場合は別途お問い合わせください。</strong>料金は予定価格です。正式リリース時に変更となる場合があります。
          </p>

          {/* Options */}
          <h3 className="font-display text-xl font-bold text-stone-900 mt-10 mb-3">追加オプション</h3>
          <div className="space-y-3">
            <AddonCard
              icon={<Building2 size={22} />}
              tone="indigo"
              title="複数拠点・部署分け"
              desc="30名以内のまま、店舗や部署ごとに従業員と勤怠を分けて管理。支店・本部・売り場ごとの集計が必要なチームに。"
              price="+ ¥800"
              priceSub="/ 月（税抜）"
              testid="addon-multi"
            />
            <AddonCard
              icon={<KeyRound size={22} />}
              tone="amber"
              title="スタッフパスコード設定"
              desc="各スタッフに4桁のパスコードを設定。共有打刻端末で他人によるなりすまし打刻を防ぎます。"
              price="+ ¥300"
              priceSub="/ 月（税抜）"
              testid="addon-pin"
              comingSoon
            />
            <AddonCard
              icon={<FileSpreadsheet size={22} />}
              tone="rose"
              title="カスタムCSV出力"
              desc="お使いの給与計算ソフトに合わせて、CSVのフォーマットを個別にカスタマイズ。列の追加・順序変更・独自項目に対応します。"
              price="+ ¥15,000"
              priceSub="（初期費用 / 1回）"
              testid="addon-customcsv"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 py-14 md:py-20">
        <div className="text-center mb-10">
          <span className="tag bg-white border border-stone-200 text-stone-600 mb-3"><HelpCircle size={12} /> FAQ</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">よくある質問</h2>
        </div>
        <div className="space-y-3">
          <Faq q="導入にどれくらい時間がかかりますか？" a="3分で完了します。企業登録 → スタッフの名前と時給を入力 → 完了です。サーバー設定や複雑な初期設定は一切ありません。" />
          <Faq q="アプリのインストールは必要ですか？" a="いりません。ブラウザでアクセスするだけで、スタッフも管理者もすぐ使えます。スマホ・PC問わず、いつもの画面のままご利用いただけます。" />
          <Faq q="スタッフは何人まで登録できますか？" a="30名まで一律料金でご利用いただけます。31名以上が必要な場合は、お問い合わせください。" />
          <Faq q="打刻を忘れた場合はどうなりますか？" a="管理画面の月次レポートからスタッフ名をタップすると、その人の打刻一覧画面が開きます。各日の出勤・退勤・休憩を後から編集できます。休憩の打ち忘れは『この人の休憩一括処理』ボタンで、デフォルト休憩を勤務日にまとめて適用することも可能です。" />
          <Faq q="給与計算ソフトとの連携はできますか？" a="月次の打刻・実労働・人件費をCSV(Excel対応)で出力できます。ほとんどの給与計算ソフトに取り込めます。" />
          <Faq q="解約はいつでもできますか？" a="はい、いつでも解約可能です。最低利用期間はありません。解約後は、データのCSVバックアップを取得いただけます。" />
          <Faq q="トライアルから本プランへの切替は自動ですか？" a="はい。2週間の無料お試し期間が終わるタイミングでカード登録のご案内をお送りします。登録いただくと、自動で通常プラン(¥1,500/月)に切り替わります。" />
          <Faq q="クーポンコードはどこで入力しますか？" a="企業登録(サインアップ)画面で、クーポンコード入力欄にコードを貼り付けてください。お問い合わせいただいた方には、運営から個別にコードをお送りすることがあります。" />
          <Faq q="複数拠点・部署で分けて管理できますか？" a="はい、オプション(+¥800/月)で店舗・部署ごとに従業員と勤怠を分けて管理できます。30名以内であれば、本部・店舗A・店舗Bといった分け方が可能です。" />
          <Faq q="カスタムCSVは何ができますか？" a="お使いの給与計算ソフトの仕様に合わせて、CSVの列順・追加項目・名称を個別にカスタマイズします。初期費用 ¥15,000 (1回のみ)です。お問い合わせフォームからご相談ください。" />
          <Faq q="31名以上で使いたい場合は？" a="31名以上のチームでも対応可能です。お問い合わせフォームから個別にご相談ください。" />
        </div>
      </section>

      {/* Contact / Lead capture */}
      <section id="contact" className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 py-14 md:py-20">
        <div className="text-center mb-10">
          <span className="tag bg-white border border-stone-200 text-stone-600 mb-3"><Send size={12} /> お問い合わせ</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-900">資料請求 / 個別相談</h2>
          <p className="text-stone-600 mt-3">31名以上・カスタムCSV・複数拠点などのご相談、もちろん通常のトライアル申込みも受け付けています。</p>
        </div>
        <LeadForm />
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="card-soft p-10 md:p-14 text-center bg-soft-mint relative overflow-hidden">
          <img src={YASAMARU_CHEER} alt="やさまる" className="absolute -right-6 -bottom-6 w-40 opacity-95 hidden md:block" />
          <h3 className="font-display text-3xl md:text-4xl font-black text-stone-900">3分で、はじめられます。</h3>
          <p className="text-stone-600 mt-3 max-w-xl mx-auto">企業名と管理者情報を入力するだけ。すぐに従業員を招待できます。</p>
          <Link to="/signup" data-testid="cta-signup" className="inline-flex items-center gap-2 mt-6 rounded-full bg-stone-900 text-white font-semibold px-8 py-3.5 hover:bg-stone-700">今すぐ企業を登録する <ChevronRight size={18} /></Link>
        </div>
      </section>

      <footer className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-8 text-stone-500 text-sm flex items-center justify-between flex-wrap gap-3">
        <span>© やさまるタイム — Yasamaru Series</span>
        <div className="flex items-center gap-4 text-xs">
          <Link to="/privacy" data-testid="footer-privacy-link" className="text-stone-500 hover:text-stone-800">プライバシーポリシー</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="text-stone-500 hover:text-stone-800">利用規約・免責事項</Link>
        </div>
      </footer>
    </div>
  );
}

function AddonCard({ icon, tone, title, desc, price, priceSub, testid, comingSoon }) {
  const colors = {
    indigo: { bg: "bg-indigo-50", text: "text-indigo-600", tag: "bg-indigo-100 text-indigo-800" },
    rose: { bg: "bg-rose-50", text: "text-rose-600", tag: "bg-rose-100 text-rose-800" },
    amber: { bg: "bg-amber-50", text: "text-amber-600", tag: "bg-amber-100 text-amber-800" },
  }[tone] || { bg: "bg-stone-50", text: "text-stone-600", tag: "bg-stone-100 text-stone-800" };
  return (
    <div className="card-soft p-6 md:p-7 border border-stone-200" data-testid={testid}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`w-12 h-12 rounded-2xl ${colors.bg} flex items-center justify-center ${colors.text} shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`tag ${colors.tag}`}>オプション</span>
            <span className="font-display text-lg font-bold text-stone-900">{title}</span>
            {comingSoon && <span className="tag bg-stone-100 text-stone-600 text-[10px]">近日公開</span>}
          </div>
          <p className="text-sm text-stone-600 mt-2 leading-relaxed">{desc}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black text-stone-900 text-2xl md:text-3xl whitespace-nowrap">{price}</div>
          <div className="text-xs text-stone-500">{priceSub}</div>
        </div>
      </div>
    </div>
  );
}

function NavLink({ href, children, testid }) {
  return <a href={href} data-testid={testid} className="px-3 py-1.5 rounded-full text-stone-600 hover:text-stone-900 hover:bg-stone-100 transition-colors">{children}</a>;
}
function MobileNav({ href, children }) {
  return <a href={href} className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-700 font-medium">{children}</a>;
}

function BigStat({ label, value, suffix, tone }) {
  const palette = {
    emerald: { card: "bg-emerald-50 border-emerald-200", label: "text-emerald-700", value: "text-emerald-700" },
    amber: { card: "bg-amber-50 border-amber-200", label: "text-amber-800", value: "text-amber-700" },
    indigo: { card: "bg-indigo-50 border-indigo-200", label: "text-indigo-700", value: "text-indigo-700" },
  }[tone];
  return (
    <div className={`rounded-2xl border ${palette.card} p-3 md:p-4 text-center shadow-sm`}>
      <div className={`text-[10px] md:text-xs font-semibold ${palette.label}`}>{label}</div>
      <div className={`font-display font-black ${palette.value} text-xl md:text-3xl leading-none mt-1`}>{value}</div>
      <div className="text-[10px] md:text-xs text-stone-700 font-medium mt-1">{suffix}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl px-3 py-2">
      <div className="text-[10px] text-stone-500">{label}</div>
      <div className="font-display font-bold text-stone-900 text-sm">{value}</div>
    </div>
  );
}

function ScreenCard({ tag, title, desc, img, testid }) {
  return (
    <div className="card-soft p-3 flex flex-col">
      <img src={img} alt={title} className="w-full rounded-xl border border-stone-200 shadow-sm aspect-[4/3] object-cover object-top" data-testid={testid} />
      <div className="px-2 pt-4 pb-2">
        <span className="text-xs font-semibold text-emerald-700">{tag}</span>
        <div className="font-display text-lg font-bold text-stone-900 mt-1">{title}</div>
        <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function Step({ n, icon, title, desc, highlight }) {
  return (
    <div className={`card-soft p-7 relative ${highlight ? "border-2 border-emerald-300" : ""}`}>
      <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-stone-900 text-white font-display font-black flex items-center justify-center shadow-md">{n}</div>
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-3 ${highlight ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-700"}`}>{icon}</div>
      <div className="font-display text-lg font-bold text-stone-900">{title}</div>
      <p className="text-sm text-stone-600 mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function FeatureCard({ icon, title, desc, tone, highlight }) {
  const ring = {
    emerald: "bg-emerald-50",
    orange: "bg-orange-50",
    rose: "bg-rose-50",
    indigo: "bg-indigo-50",
  }[tone] || "bg-stone-50";
  return (
    <div className="card-soft p-6 relative">
      {highlight && <span className="tag bg-amber-100 text-amber-800 absolute top-4 right-4 text-[10px]">NEW</span>}
      <div className={`w-12 h-12 rounded-2xl ${ring} flex items-center justify-center mb-4`}>{icon}</div>
      <div className="font-display text-xl font-bold text-stone-900">{title}</div>
      <p className="text-stone-600 mt-2 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

function Faq({ q, a }) {
  return (
    <details className="card-soft p-5 group" data-testid={`faq-${q.slice(0, 8)}`}>
      <summary className="cursor-pointer font-display font-bold text-stone-900 flex items-center justify-between gap-3 list-none">
        <span>{q}</span>
        <span className="text-stone-400 group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
      </summary>
      <p className="text-stone-600 text-sm mt-3 leading-relaxed">{a}</p>
    </details>
  );
}

function LeadForm() {
  const [form, setForm] = React.useState({ name: "", company: "", email: "", phone: "", interest: "trial", message: "" });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const api = (await import("@/lib/api")).default;
      await api.post("/leads", form);
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr((typeof detail === "string" && detail) || "送信に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card-soft p-8 text-center" data-testid="lead-form-success">
        <div className="font-display text-2xl font-black text-emerald-700">送信完了</div>
        <p className="text-stone-600 mt-2">お問い合わせありがとうございます！1〜2営業日以内にご返信します。</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-soft p-6 md:p-8 space-y-4" data-testid="lead-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Lbl label="お名前" required>
          <input data-testid="lead-name" required type="text" value={form.name} onChange={upd("name")} className="inp" />
        </Lbl>
        <Lbl label="会社名 / 屋号">
          <input data-testid="lead-company" type="text" value={form.company} onChange={upd("company")} className="inp" />
        </Lbl>
        <Lbl label="メールアドレス" required>
          <input data-testid="lead-email" required type="email" value={form.email} onChange={upd("email")} className="inp" />
        </Lbl>
        <Lbl label="電話番号(任意)">
          <input data-testid="lead-phone" type="tel" value={form.phone} onChange={upd("phone")} className="inp" />
        </Lbl>
      </div>
      <Lbl label="ご相談内容">
        <select data-testid="lead-interest" value={form.interest} onChange={upd("interest")} className="inp">
          <option value="trial">トライアルを始めたい</option>
          <option value="multi_location">複数拠点・部署分けについて</option>
          <option value="staff_pin">スタッフパスコード設定について</option>
          <option value="custom_csv">カスタムCSV出力について</option>
          <option value="31plus">31名以上の利用を相談したい</option>
          <option value="other">その他</option>
        </select>
      </Lbl>
      <Lbl label="メッセージ(任意)">
        <textarea data-testid="lead-message" rows={4} value={form.message} onChange={upd("message")} className="inp" />
      </Lbl>
      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm">{err}</div>}
      <button type="submit" data-testid="lead-submit" disabled={busy} className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3.5 inline-flex items-center justify-center gap-2">
        <Send size={16} /> {busy ? "送信中..." : "送信する"}
      </button>
      <style>{`.inp{width:100%; padding:.6rem .85rem; border-radius:.75rem; border:1px solid #E7E5E4; background:#FAFAF9; outline:none; font-size:.875rem; font-family:inherit;} .inp:focus{background:#fff; box-shadow:0 0 0 3px rgba(74,222,128,.25);}`}</style>
    </form>
  );
}

function Lbl({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-stone-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
